"use client";

import {useEffect, useState} from "react";
import {
    addDays,
    addMinutes,
    addMonths,
    format,
    getDay,
    isAfter,
    isBefore,
    isEqual, isFuture,
    isLastDayOfMonth,
    isToday,
    subMonths
} from "date-fns";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {clsx} from "clsx";
import {weekdaysShortNames} from "@/lib/helpers/calendarHelper";
import Link from "next/link";
import {Puff} from "react-loader-spinner";
import useAxiosAuth from "@/lib/hooks/useAxiosAuth";
import CWFButton from "@/components/ui/CWFButton";
import ButtonPayment from "@/components/Payments/ButtonPayment";

export default function TimePicker({bookingTimes, length, meetingUri, username, offerId, userId, type="RES"}){
    const currentDate = new Date();
    const axios = useAxiosAuth();
    const [activeMonthDate, setActiveMonthDate] = useState(currentDate);
    const [activeMonthIndex, setActiveMonthIndex] = useState(activeMonthDate.getMonth());
    const [activeYear, setActiveYear] = useState(currentDate.getFullYear());
    const [selectedDay, setSelectedDay] = useState(null);
    const [busySlots, setBusySlots] = useState([]);
    const [busySlotsLoaded, setBusySlotsLoaded] = useState(false);
    const [selectedDate, setSelectedDate] = useState(false);

    // Migration de l'ancien format vers le nouveau
    const migratedBookingTimes = migrateBookingTimes(bookingTimes);

    useEffect(()=>{
        if(selectedDay){
            setBusySlotsLoaded(false);
            setBusySlots([]);

            axios.get(`reservations/${userId}`)
                .then(res => {
                    setBusySlots(res.data.data);
                    setBusySlotsLoaded(true);
                })
        }

    }, [selectedDay]);

    // Fonction de migration de l'ancien format vers le nouveau
    function migrateBookingTimes(times){
        if (!times) return {};

        const migrated = {};
        Object.keys(times).forEach(day => {
            const dayData = times[day];

            // Détecte l'ancien format (avec from/to directement)
            if (dayData.hasOwnProperty('from') && dayData.hasOwnProperty('to') && !dayData.hasOwnProperty('slots')) {
                migrated[day] = {
                    active: dayData.active || false,
                    slots: [{
                        from: dayData.from || '09:00',
                        to: dayData.to || '17:00'
                    }]
                };
            } else {
                // Nouveau format
                migrated[day] = dayData;
            }
        });

        return migrated;
    }

    function withinBusySlots(time){
        const bookingFrom = time;
        const bookingTo = addMinutes(new Date(time), length);

        if(busySlots.length === 0) return false;

        for(let busySlot of busySlots){
            const busyFrom = new Date(busySlot.start_time);
            const busyTo = new Date(busySlot.end_time);

            if (isAfter(bookingTo, busyFrom) && isBefore(bookingTo, busyTo)) {
                return true;
            }
            if (isAfter(bookingFrom, busyFrom) && isBefore(bookingFrom, busyTo)) {
                return true;
            }
            if (isEqual(bookingFrom, busyFrom)) {
                return true;
            }
            if (isEqual(bookingTo, busyTo)) {
                return true;
            }
        }
        return false;
    }

    const firstDayOfCurrentMonth = new Date(activeYear, activeMonthIndex, 1);
    const firstDayOfCurrentMonthWeekdayIndex = getDay(firstDayOfCurrentMonth);
    const emptyDaysCount = firstDayOfCurrentMonthWeekdayIndex === 0 ? 6 : firstDayOfCurrentMonthWeekdayIndex - 1;
    const emptyDaysArr = (new Array(emptyDaysCount)).fill('', 0, emptyDaysCount);
    const daysNumbers = [firstDayOfCurrentMonth];
    do{
        const lastAddedDay = daysNumbers[daysNumbers.length - 1];
        daysNumbers.push(addDays(lastAddedDay, 1));

    }while(!isLastDayOfMonth(daysNumbers[daysNumbers.length - 1]));

    let selectedDayConfig = null;
    const bookingHours = [];

    if(selectedDay){
        const weekdayNameIndex = format(selectedDay, 'EEEE').toLowerCase();
        selectedDayConfig = migratedBookingTimes?.[weekdayNameIndex];

        if(selectedDayConfig && selectedDayConfig.active && selectedDayConfig.slots){
            // Parcourir tous les slots (créneaux) de la journée
            selectedDayConfig.slots.forEach(slot => {
                const [hoursFrom, minutesFrom] = slot.from.split(":");
                const selectedDayFrom = new Date(selectedDay);
                selectedDayFrom.setHours(parseInt(hoursFrom));
                selectedDayFrom.setMinutes(parseInt(minutesFrom));

                const selectedDayTo = new Date(selectedDay);
                const [hoursTo, minutesTo] = slot.to.split(':');
                selectedDayTo.setHours(parseInt(hoursTo));
                selectedDayTo.setMinutes(parseInt(minutesTo));

                let a = selectedDayFrom;
                // Générer des créneaux toutes les 15 minutes au lieu de 60
                while(isBefore(addMinutes(a, length), selectedDayTo) || isEqual(addMinutes(a, length), selectedDayTo)) {
                    if (!withinBusySlots(a)) {
                        bookingHours.push(a);
                    }
                    a = addMinutes(a, 15); // Changé de 60 à 15 minutes
                }
            });

            // Trier les heures de réservation par ordre chronologique
            bookingHours.sort((a, b) => a.getTime() - b.getTime());
        }
    }

    function prevMonth(){
        setActiveMonthDate(prev => {
            const newActiveMonthDate = subMonths(prev, 1);
            setActiveMonthIndex(newActiveMonthDate.getMonth());
            setActiveYear(newActiveMonthDate.getFullYear());
            return newActiveMonthDate;
        });
    }

    function nextMonth(){
        setActiveMonthDate(prev => {
            const newActiveMonthDate = addMonths(prev, 1);
            setActiveMonthIndex(newActiveMonthDate.getMonth());
            setActiveYear(newActiveMonthDate.getFullYear());
            return newActiveMonthDate;
        });
    }

    function handleDayClick(day){
        setSelectedDay(day);
    }

    return (
        <div>
            <div className={"flex flex-col items-center md:flex-row "}>
                <div className={"p-8"}>
                    <div className={"flex items-center"}>
                     <span className="grow">
                         {new Date(activeYear, activeMonthIndex, 1).toLocaleDateString('fr-FR', {month: 'long'}).replace(/^\w/, (c) => c.toUpperCase())} {activeYear}
                     </span>
                        <button onClick={prevMonth}>
                            <ChevronLeft/>
                        </button>
                        <button onClick={nextMonth}>
                            <ChevronRight/>
                        </button>
                    </div>
                    <div className="inline-grid gap-2 grid-cols-7 mt-2">
                        {weekdaysShortNames.map((weekdayShortName) => (
                            <div
                                key={weekdayShortName}
                                className="text-center uppercase text-sm text-gray-500 font-bold">
                                {weekdayShortName}
                            </div>
                        ))}
                        {emptyDaysArr.map((empty, emptyIndex) => (
                            <div key={emptyIndex}/>
                        ))}
                        {daysNumbers.map(n => {
                            const weekdayNameIndex = format(n, "EEEE").toLowerCase()
                            const weekdayConfig = migratedBookingTimes?.[weekdayNameIndex];
                            const isActiveInBookingTimes = weekdayConfig?.active;
                            const canBeBooked = (isFuture(n) || isToday(n)) && isActiveInBookingTimes;
                            const isSelected = selectedDay && isEqual(n, selectedDay);
                            return (
                                <div
                                    key={n.toISOString()}
                                    className="text-center text-sm text-gray-400 font-bold ">
                                    <button
                                        disabled={!canBeBooked}
                                        onClick={() => handleDayClick(n)}
                                        className={clsx(
                                            "w-8 h-8 rounded-full inline-flex items-center justify-center",
                                            canBeBooked && !isSelected ? "bg-amber-100 text-primary" : "",
                                            isToday(n) && !isSelected ? "bg-gray-200 text-gray-500" : '',
                                            isSelected ? "bg-primary text-white" : "",
                                        )}
                                    >
                                        {format(n, 'd')}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {selectedDay && (
                    <div className="pt-8 pl-2 overflow-auto pr-8 w-48">
                        <p className="text-left text-sm">
                            {selectedDay.toLocaleDateString('fr-FR', {month: 'long', day: 'numeric', year: 'numeric'})}
                        </p>
                        <div className="grid gap-2 mt-2 max-h-52 ">
                            {!busySlotsLoaded && (
                                <div className="flex justify-center py-4">
                                    <Puff color="#F0A202"/>
                                </div>
                            )}
                            {busySlotsLoaded && bookingHours.map(bookingTime => (
                                <div key={bookingTime.toISOString()}>
                                    <button
                                        className={`w-full block border-2 rounded-lg border-primary  font-semibold ${bookingTime.toISOString() === selectedDate ? 'bg-primary text-white' : 'text-primary bg-white'}`}
                                        onClick={() => setSelectedDate(bookingTime.toISOString())}
                                    >
                                        {format(bookingTime, 'HH:mm')}
                                    </button>
                                </div>
                            ))}
                            <div className="mb-8">&nbsp;</div>
                        </div>
                    </div>
                )}
            </div>
            {selectedDate && (
                <div className={"flex items-center justify-end"}>
                    {type === "RES"
                        ? <ButtonPayment disabled={false} type={"RES"} offer_id={offerId} start_time={selectedDate} user_to={userId} name={"Réserver et payer"} redirect_module={"reservation"}/>
                        : <ButtonPayment disabled={false} type={"RESU"} start_time={selectedDate} user_to={userId} name={"Réserver et payer"} redirect_module={"reservation"}/>
                    }
                </div>
            )}
        </div>
    )
}